import { APIClient } from '@/services'
import { CommunityComment } from '@/types'
import React from 'react'
import { TypeGuards } from '@codeleap/types'
import { replyStore } from '@/stores'

type UseCommentSubmitProps = {
  itemId?: number
  value: string
  onSuccess?: () => void
  onError?: (error: string) => void
}

export const useCommentSubmit = ({ itemId, value, onSuccess, onError }: UseCommentSubmitProps) => {
  const [isSending, setIsSending] = React.useState(false)
  const [error, setError] = React.useState('')

  const createComment = APIClient.Community.communityCommentsManager.useCreate({
    onListsWithFilters: { item_id: itemId },
  })

  const handleSubmit = React.useCallback(async () => {
    const authorized = APIClient.UnknownProfile.maybeOpenRegistrationWall()
    if (!authorized) return

    if (!itemId || !value.trim() || isSending) return

    setIsSending(true)

    const replyingTo = replyStore.getReplyTo()
    const isReply = TypeGuards.isNumber(replyingTo?.reply_to)
    const mention = replyingTo?.profile?.display_name ? `@${replyingTo.profile.display_name}` : ''

    const data = {
      item: itemId,
      reply_to: isReply ? replyingTo?.reply_to : replyingTo?.id,
      text: replyingTo ? [mention, value].filter(Boolean).join(' ') : value,
    } as Partial<CommunityComment>

    try {
      if (replyingTo) {
        await APIClient.Community.communityCommentsManager.actions.createReply(data)
      } else {
        await createComment.create(data)
      }

      APIClient.Community.communityOperations.mutations.updateCommentCount({ postId: itemId, value: 1 })

      logger.log('Comment sent successfully', { itemId, value })
      onSuccess?.()
    } catch (err) {
      logger.error('Error sending comment:', err)

      const errorData = err?.response?.data?.text || err?.data?.text || err?.message || 'Failed to send comment'

      setError(errorData)
      onError?.(errorData)
    } finally {
      setIsSending(false)
      replyStore.resetReplyState()
    }
  }, [value, createComment, itemId, isSending, onSuccess, onError])

  const clearError = React.useCallback(() => {
    setError('')
  }, [])

  return {
    isSending,
    error,
    handleSubmit,
    clearError,
  }
}
